<template>
  <Teleport to="body">
    <div
      v-if="!toastStore.isDisabledHintBox && isReady"
      class="box-container"
      :style="{ bottom: positionBottom, left: positionLeft }"
      ref="refBoxContainer"
    >
      <slot></slot>
      <div class="box-wrapper">
        <div ref="hintBox" class="hint-box">
          <div class="hint-box-border">
            <p class="psmall accent" v-html="messageDisplay" />
          </div>
        </div>
        <div class="hint-box-tail" />
      </div>
    </div>
  </Teleport>
</template>
<script setup lang="ts">
import { onMounted, ref, watch, computed, nextTick, onUnmounted } from 'vue';

import { storeToRefs } from 'pinia';

import { useToastStore } from '@/stores/toast.js';

const props = defineProps<{
  refTarget: HTMLElement;
  messageHintBox?: string;
}>();

const toastStore = useToastStore();
const { hintBoxHtml, showHintBox } = storeToRefs(toastStore);

const refBoxContainer = ref<HTMLElement>();
const hintBox = ref(null);

const positionBottom = ref<string>('0px');
const positionLeft = ref<string>('0px');
const isReady = ref<boolean>(true);

const messageDisplay = computed(() => {
  return props.messageHintBox ? props.messageHintBox : hintBoxHtml.value;
});

const updatePosition = () => {
  nextTick(() => {
    if (!refBoxContainer.value || !props.refTarget) {
      isReady.value = false;
      return;
    }

    const rectTarget = props.refTarget.getBoundingClientRect();
    const hintBoxRef = refBoxContainer.value.getBoundingClientRect();

    const boxWidth = hintBoxRef.width || refBoxContainer.value.offsetWidth;

    const viewportWidth = window.innerWidth;
    const offsetParent = refBoxContainer.value.offsetParent;
    const offsetParentRect = offsetParent
      ? offsetParent.getBoundingClientRect()
      : { left: 0, top: 0 };

    const margin = 4;

    const targetCenterXRel =
      rectTarget.left - offsetParentRect.left + rectTarget.width / 2;

    let leftPosition = targetCenterXRel - boxWidth / 2;

    const absoluteLeft = leftPosition + offsetParentRect.left;
    if (absoluteLeft < margin) {
      leftPosition = margin - offsetParentRect.left;
    } else if (absoluteLeft + boxWidth > viewportWidth - margin) {
      leftPosition = viewportWidth - boxWidth - margin - offsetParentRect.left;
    }

    const tailLeftPosition = targetCenterXRel - leftPosition;
    refBoxContainer.value.style.setProperty(
      '--tail-left',
      `${tailLeftPosition}px`
    );

    positionLeft.value = `${leftPosition}px`;
    positionBottom.value = rectTarget.height / 2 + 60 + 'px';
    isReady.value = true;
  });
};

onMounted(() => {
  window.addEventListener('resize', updatePosition);
  updatePosition();
  setTimeout(() => {
    showHintBox.value = '';
  }, 5000);
});

onUnmounted(() => {
  window.removeEventListener('resize', updatePosition);
  isReady.value = false;
});

watch(
  () => hintBoxHtml.value,
  () => {
    updatePosition();
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.box-container {
  position: fixed;
  display: inline-block;
  z-index: 9999;
}

.box-wrapper {
  position: relative;
  display: inline-block;
}

.hint-box {
  display: flex;
  align-items: flex-start;
  width: auto;
  max-width: calc(100dvw - 2rem);
  border-radius: 16px;
  z-index: 1;
}

.hint-box-border {
  position: relative;
  z-index: 1;
  padding: 0.5rem 0.75rem;
  background-color: var(--surface-default);
  color: var(--font-default);
  border-radius: 16px;
  backdrop-filter: blur(4px);
}

.hint-box-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 16px;
  background: var(--border-hintbox);
  opacity: 0.8;
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: destination-out;
  mask-composite: exclude;
  -webkit-mask-composite: xor;
  z-index: -1;
}

.info-icon {
  position: relative;
  fill: var(--font-white);
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.hint-box-tail {
  position: absolute;
  bottom: -0.25rem;
  left: var(--tail-left, 50%);
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 0.325rem solid transparent;
  border-right: 0.325rem solid transparent;
  border-top: 0.325rem solid var(--tail-hintbox);
}

@media (max-width: 768px) {
  .box-container {
    left: 3rem;
  }
}
</style>
